import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { getApiErrorMessage } from "@/lib/apiError";
import type { LeadSource } from "@/services/moderators/generated/model";
import {
  getModeratorsCompaniesSourceBreakdownRetrieveQueryKey,
  getModeratorsLeadSourcesListQueryKey,
  useModeratorsLeadSourcesCreate,
  useModeratorsLeadSourcesDestroy,
  useModeratorsLeadSourcesList,
  useModeratorsLeadSourcesPartialUpdate,
} from "@/services/moderators/generated/moderators-companies/moderators-companies";
import "./ModeratorPage.css";
import "./LeadSources.css";

/** Types a named source can have ("Other" is free text at registration). */
const NAMED_SOURCE_TYPES = ["institution", "event", "partner"] as const;
type NamedSourceType = (typeof NAMED_SOURCE_TYPES)[number];

const isNamedSourceType = (value: string): value is NamedSourceType =>
  (NAMED_SOURCE_TYPES as readonly string[]).includes(value);

const RELATIONSHIPS = ["free_referral", "paid_referral", "revenue_share", "other"] as const;
type Relationship = (typeof RELATIONSHIPS)[number];
const PAID_RELATIONSHIPS: readonly Relationship[] = ["paid_referral", "revenue_share"];
const isPaid = (r: string) => (PAID_RELATIONSHIPS as readonly string[]).includes(r);

const ModeratorLeadSourcesPage = () => (
  <ModeratorLayout>
    {(moderator) => <LeadSourcesContent canManage={moderator.role === "SUPERMODERATOR"} />}
  </ModeratorLayout>
);

/**
 * Named lead sources offered on the registration form (WP1.9). Every
 * moderator can see the list; only super moderators add, rename,
 * deactivate or delete sources.
 */
const LeadSourcesContent = ({ canManage }: { canManage: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const typeLabel = (value: string) => (isNamedSourceType(value) ? t(`leadSources.types.${value}`) : value);
  const queryClient = useQueryClient();
  const listParams = { page_size: 100 };
  const { data, isLoading, error } = useModeratorsLeadSourcesList(listParams);
  const sources = unwrapPaginated<LeadSource>(data);

  const [name, setName] = useState("");
  const [type, setType] = useState<string>(NAMED_SOURCE_TYPES[0]);
  const [relationship, setRelationship] = useState<string>(RELATIONSHIPS[0]);
  const [commission, setCommission] = useState<string>("");
  const [disclosure, setDisclosure] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getModeratorsLeadSourcesListQueryKey(listParams) });
    queryClient.invalidateQueries({ queryKey: getModeratorsCompaniesSourceBreakdownRetrieveQueryKey() });
  };
  const resetForm = () => {
    setName("");
    setType(NAMED_SOURCE_TYPES[0]);
    setRelationship(RELATIONSHIPS[0]);
    setCommission("");
    setDisclosure("");
    setEditingId(null);
    setFormError(null);
  };

  const createMutation = useModeratorsLeadSourcesCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        resetForm();
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("leadSources.addError"))),
    },
  });
  const updateMutation = useModeratorsLeadSourcesPartialUpdate({
    mutation: {
      onSuccess: () => {
        invalidate();
        resetForm();
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("leadSources.saveError"))),
    },
  });
  const deleteMutation = useModeratorsLeadSourcesDestroy({
    mutation: {
      onSuccess: invalidate,
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("leadSources.deleteError"))),
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError(t("leadSources.nameRequired"));
      return;
    }
    const commissionString = commission.trim() === "" ? null : commission.trim();
    const body = {
      name: name.trim(),
      type,
      relationship,
      commissionPercent: isPaid(relationship) ? commissionString : null,
      disclosureText: disclosure.trim(),
    };
    if (editingId !== null) updateMutation.mutate({ id: editingId, data: body });
    else createMutation.mutate({ data: body });
  };

  const startEdit = (src: LeadSource) => {
    setEditingId(src.id);
    setName(src.name);
    setType(src.type);
    setRelationship(src.relationship ?? RELATIONSHIPS[0]);
    setCommission(src.commissionPercent != null ? String(src.commissionPercent) : "");
    setDisclosure(src.disclosureText ?? "");
    setFormError(null);
  };

  const toggleActive = (src: LeadSource) => {
    setFormError(null);
    updateMutation.mutate({ id: src.id, data: { isActive: !(src.isActive ?? true) } });
  };

  const remove = (src: LeadSource) => {
    setFormError(null);
    if (window.confirm(t("leadSources.deleteConfirm", { name: src.name }))) deleteMutation.mutate({ id: src.id });
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("leadSources.title")}</h1>
          <p className="mod-page__subtitle">{t("leadSources.subtitle")}</p>
        </div>
      </header>

      {canManage ? (
        <form className="mod-sources__form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="mod-page__input"
            placeholder={t("leadSources.sourceName")}
            maxLength={150}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t("leadSources.sourceName")}
          />
          <select
            className="mod-page__select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label={t("leadSources.sourceType")}
          >
            {NAMED_SOURCE_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`leadSources.types.${value}`)}
              </option>
            ))}
          </select>
          <select
            className="mod-page__select"
            value={relationship}
            onChange={(e) => {
              setRelationship(e.target.value);
              if (!isPaid(e.target.value)) setCommission("");
            }}
            aria-label={t("leadSources.relationship.label")}
            title={t("leadSources.relationship.help")}
          >
            {RELATIONSHIPS.map((value) => (
              <option key={value} value={value}>
                {t(`leadSources.relationship.${value}`)}
              </option>
            ))}
          </select>
          {isPaid(relationship) ? (
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="mod-page__input"
              placeholder={t("leadSources.commission.label")}
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              aria-label={t("leadSources.commission.label")}
              title={t("leadSources.commission.help")}
            />
          ) : null}
          <input
            type="text"
            className="mod-page__input"
            placeholder={t("leadSources.disclosure.label")}
            value={disclosure}
            onChange={(e) => setDisclosure(e.target.value)}
            aria-label={t("leadSources.disclosure.label")}
            title={t("leadSources.disclosure.help")}
            maxLength={500}
          />
          <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={saving}>
            {saving ? t("common:actions.saving") : editingId !== null ? t("common:actions.save") : t("leadSources.add")}
          </button>
          {editingId !== null ? (
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={resetForm}>
              {t("common:actions.cancel")}
            </button>
          ) : null}
        </form>
      ) : (
        <p className="mod-page__card-meta">{t("leadSources.superOnly")}</p>
      )}
      {formError ? (
        <p className="mod-page__error" role="alert">
          {formError}
        </p>
      ) : null}

      {isLoading ? <div className="mod-page__loading">{t("leadSources.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("leadSources.loadError")}</div> : null}
      {!isLoading && !error && sources.length === 0 ? (
        <div className="mod-page__empty">{t("leadSources.empty")}</div>
      ) : null}

      {sources.length > 0 ? (
        <div className="mod-page__card">
          <div className="mod-page__card-body mod-sources__table-wrap">
            <table className="mod-sources__table">
              <thead>
                <tr>
                  <th>{t("common:labels.name")}</th>
                  <th>{t("leadSources.table.type")}</th>
                  <th>{t("leadSources.table.relationship")}</th>
                  <th>{t("common:labels.status")}</th>
                  <th className="mod-sources__count">{t("leadSources.table.companies")}</th>
                  {canManage ? <th>{t("common:labels.actions")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {sources.map((src) => (
                  <tr key={src.id}>
                    <td>{src.name}</td>
                    <td>{typeLabel(src.type)}</td>
                    <td>{t(`leadSources.relationship.${(src.relationship ?? "free_referral") as Relationship}`)}</td>
                    <td>
                      <span
                        className={`mod-page__chip ${src.isActive === false ? "mod-page__chip--warning" : "mod-page__chip--success"}`}
                      >
                        {src.isActive === false ? t("shared.inactive") : t("shared.active")}
                      </span>
                    </td>
                    <td className="mod-sources__count">{src.companyCount}</td>
                    {canManage ? (
                      <td>
                        <div className="mod-sources__actions">
                          <button
                            type="button"
                            className="mod-page__btn mod-page__btn--ghost"
                            onClick={() => startEdit(src)}
                          >
                            {t("common:actions.edit")}
                          </button>
                          <button
                            type="button"
                            className="mod-page__btn mod-page__btn--ghost"
                            onClick={() => toggleActive(src)}
                            disabled={updateMutation.isPending}
                          >
                            {src.isActive === false ? t("shared.activate") : t("shared.deactivate")}
                          </button>
                          <button
                            type="button"
                            className="mod-page__btn mod-page__btn--danger"
                            onClick={() => remove(src)}
                            disabled={deleteMutation.isPending || src.companyCount > 0}
                            title={src.companyCount > 0 ? t("leadSources.inUse") : undefined}
                          >
                            {t("common:actions.delete")}
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ModeratorLeadSourcesPage;
